import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { useBookingQuery } from "@/query/bookingQuery"
import { useClientNameQuery } from "@/query/clientQuery"
import { useShopDataQuery } from "@/query/shopDataQuery"
import {
  addServiceItem,
  setBookingId,
  setSelectedClientId,
} from "@/store/cashRegisterReducers/cashRegisterSlice"
import { useAppDispatch, useAppSelector } from "@/store/store"
import { getPrestatere } from "@/utils/functions/getPrestatere"
import { Card, CardContent } from "@okeys/shared-react-components"
import { BookingTable } from "@okeys/shared-utils"
import { Calendar, Clock, Euro, User } from "lucide-react"
import React from "react"

// Composant pour afficher le nom du client
const ClientName: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { client, isLoading, isError } = useClientNameQuery(clientId)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-muted-foreground" />
        <div className="animate-pulse bg-gray-200 h-4 w-24 rounded"></div>
      </div>
    )
  }

  if (isError || !client) {
    return (
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-gray-900 text-sm">
          Client ID: {clientId}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <User className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium text-card-foreground text-sm">
        {client.name}
      </span>
    </div>
  )
}

// Composant pour afficher le nom du prestataire
const PrestataireName: React.FC<{ prestatereId: string }> = ({
  prestatereId,
}) => {
  const { shopData, pendingShopData, errorShopData } = useShopDataQuery()

  if (pendingShopData || !shopData) {
    return (
      <Badge className="bg-primary/20 text-primary text-xs">
        <div className="animate-pulse bg-gray-300 h-3 w-16 rounded"></div>
      </Badge>
    )
  }

  if (errorShopData) {
    return (
      <Badge className="bg-gray-100 text-gray-600 text-xs">
        ID: {prestatereId.slice(0, 8)}...
      </Badge>
    )
  }

  const prestataire = getPrestatere(shopData, prestatereId)

  if (!prestataire) {
    return (
      <Badge className="bg-gray-100 text-gray-600 text-xs">
        Prestataire inconnu
      </Badge>
    )
  }

  return (
    <Badge className="bg-primary/20 text-primary text-xs">
      {prestataire.givenName} {prestataire.familyName}
    </Badge>
  )
}

export const ReservationSelectionList: React.FC = () => {
  const {
    booking,
    pendingBooking: pendingReservation,
    errorBooking: errorReservation,
  } = useBookingQuery()

  const dispatch = useAppDispatch()

  const { bookingId: currentBookingId } = useAppSelector(
    (state) => state.cashRegister
  )

  const handleReservationSelect = (reservation: BookingTable) => {
    const bookingId = reservation.bookingId
    if (currentBookingId !== bookingId) {
      if (reservation.clientId || undefined) {
        dispatch(setSelectedClientId(reservation.clientId || undefined))
      }
      for (const service of reservation.services || []) {
        dispatch(
          addServiceItem({
            serviceItem: service,
            promotions: [],
            availableServices: [],
          })
        )
      }
      dispatch(setBookingId(bookingId))
    }
  }

  // Filtrer les réservations qui peuvent être encaissées (seulement en attente et dont la date de début est passée)
  const encashableReservations = (booking || []).filter((res) => {
    if (res.state !== "pending") return false

    const startTime = new Date(res.scheduledStart || "")
    const now = new Date()

    // Ne garder que les réservations dont le début est déjà passé
    return startTime <= now
  })

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  }

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (pendingReservation) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
        <span className="ml-2 text-gray-600">
          Chargement des rendez-vous...
        </span>
      </div>
    )
  }

  if (errorReservation) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-2">Erreur lors du chargement</div>
        <p className="text-sm text-gray-600">
          Impossible de charger les rendez-vous
        </p>
      </div>
    )
  }

  if (encashableReservations.length === 0) {
    return (
      <div className="text-center py-8">
        <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-gray-600 mb-2">Aucun rendez-vous à encaisser</p>
        <p className="text-sm text-gray-500">
          Seuls les rendez-vous en attente peuvent être encaissés
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4 h-full bg-background">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-card-foreground">
          Rendez-vous à encaisser
        </h3>
        <Badge variant="secondary" className="bg-primary/20 text-primary">
          {encashableReservations.length} disponible(s)
        </Badge>
      </div>

      <div className="overflow-y-auto space-y-3 h-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-1">
          {encashableReservations.map((res) => {
            const isSelected = currentBookingId === res.bookingId

            return (
              <Card
                withShadowHover
                key={res.bookingId}
                onClick={() => handleReservationSelect(res)}
                className={cn(isSelected && "border-primary")}
              >
                <CardContent>
                  <div>
                    {/* En-tête avec client et prestataire */}
                    <div className="flex items-center justify-between">
                      <ClientName clientId={res.clientId || ""} />
                      <PrestataireName prestatereId={res.prestatereId} />
                    </div>

                    {/* Date et heure */}
                    <div className="text-sm text-muted-foreground flex flex-col gap-1 pt-1">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(res.scheduledStart as string)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>
                          {formatTime(res.scheduledStart as string)} -{" "}
                          {formatTime(res.scheduledEnd as string)}
                        </span>
                      </div>
                    </div>

                    {/* Total et indicateur de sélection */}
                    <div className="flex items-center justify-end pt-2">
                      <div className="flex items-center gap-1">
                        <Euro className="h-4 w-4 text-primary" />
                        <span className="font-bold text-primary">
                          {((res.total || 0) as number).toFixed(2)}€
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </div>
  )
}
